import { useEffect, useCallback, useRef } from 'react';
import { RoomEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import { useSettings } from '../context/SettingsContext';
import { shouldBlockInvite, extractDomain } from '../utils/inviteFiltering';
import timInformationService from '../services/timInformationService';
import logger from '../utils/logger';

/**
 * Hook to filter and auto-reject room invites based on Incoming Invites settings.
 *
 * 1. Listens for new room invites via RoomEvent.MyMembership.
 * 2. Classifies group entries via the Messenger-Proxy isInsurance endpoint
 *    (A_26445-01) to implement A_26390 (isInsuredPerson group).
 * 3. Applies mode-aware fail-mode on classification failures:
 *    - AllowAll mode: fail-open (allow the invite)
 *    - BlockAll mode: fail-closed (block the invite)
 * 4. Guards against logout races: if the client is gone or the room is no
 *    longer in the invite state after an async lookup, bails out.
 *
 * Auto-rejection prevents blocked invites from appearing in the room list.
 */
const useInviteFiltering = () => {
  const client = useMatrixClient();
  const { settings, isReady } = useSettings();

  // Keep settings in a ref so handleInvite can read the latest value without
  // re-subscribing the Matrix listener on every settings change. Re-subscribing
  // (off/on) opens a race window where an incoming invite can land between the
  // off and on calls and be missed entirely.
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  // isReady flips true only after /sync delivers m.secret_storage / m.account_data
  // and SettingsContext has loaded the real server settings. Before that, the
  // hook would evaluate against DEFAULT_SETTINGS (allow-all, empty lists) and
  // wrongly auto-reject invites that should pass per the user's BlockAll +
  // allowedList. The ref pattern matches settingsRef so we don't rebind the
  // RoomEvent.MyMembership listener every time isReady flips.
  const isReadyRef = useRef(isReady);
  useEffect(() => { isReadyRef.current = isReady; }, [isReady]);

  const handleInvite = useCallback(
    async (room, membership) => {
      // UC12X-TRACE: log every invocation so we can see the full sequence
      // of membership events on C across the test scenario lifetime.
      // Each line includes deviceId (inferred from client) + roomId + the
      // membership being processed + isReady state at this moment.
      const _selfMxid = client && typeof client.getUserId === 'function' ? client.getUserId() : 'unknown';
      const _deviceShort = client && typeof client.getDeviceId === 'function' ? client.getDeviceId() : 'unknown';
      logger.debug(`[UC12X-TRACE] handleInvite called: room=${room.roomId} membership=${membership} isReady=${isReadyRef.current} self=${_selfMxid} dev=${_deviceShort}`);
      if (membership !== 'invite') {
        logger.debug(`[UC12X-TRACE] handleInvite early-return: not an invite (membership=${membership})`);
        return;
      }

      // Settings-sync race guard: defer the decision until SettingsContext has
      // loaded server state. Without this, gematik cert TIM_V2_PRO_AF_050110/
      // 050121 see Allow-User → BlockAll invites incorrectly rejected during
      // the load window. A separate effect below sweeps any invites that were
      // already in 'invite' state when isReady flips false → true and re-runs
      // this handler for each — the bare MyMembership listener does NOT fire
      // when only settings change.
      if (!isReadyRef.current) {
        logger.debug(
          `[useInviteFiltering] Settings not yet synced; deferring invite decision for room ${room.roomId}`,
        );
        logger.debug(`[UC12X-TRACE] DEFERRED: settings not ready for room ${room.roomId}`);
        return;
      }

      const currentSettings = settingsRef.current || {};

      const myUserId = client.getUserId();
      const inviteEvent = room.currentState.getStateEvents('m.room.member', myUserId);
      if (!inviteEvent) {
        logger.warn('[useInviteFiltering] No invite event found for room', room.roomId);
        return;
      }

      const inviterUserId = inviteEvent.getSender();
      if (!inviterUserId) {
        logger.warn('[useInviteFiltering] No inviter found in invite event');
        return;
      }

      // Async group resolver — caches results per homeserver via timInformationService.
      const groupResolver = async (userId, groupName) => {
        if (groupName !== 'isInsuredPerson') return null;
        const hs = extractDomain(userId);
        if (!hs) return null;
        try {
          return await timInformationService.isInsurance(hs);
        } catch (err) {
          logger.warn(`[useInviteFiltering] isInsurance lookup failed for ${hs}: ${err?.message}`);
          return null; // caller will apply fail-mode
        }
      };

      // Evaluate sync first (covers user/domain matches without any async work).
      let result = shouldBlockInvite(inviterUserId, currentSettings);

      // If there are group entries in the active list, re-evaluate with the async resolver.
      // We detect this by checking the active list directly to avoid redundant lookups.
      const activeList = currentSettings.firstContactDefaultPermission === 'allow-all'
        ? (currentSettings.firstContactBlockedList || [])
        : (currentSettings.firstContactAllowedList || []);
      const hasGroupEntries = activeList.some(i => i && i.type === 'group');

      if (hasGroupEntries) {
        // Run all group resolutions in parallel, then apply logic synchronously.
        const groupEntries = activeList.filter(i => i && i.type === 'group' && i.value);
        const results = await Promise.all(
          groupEntries.map(g => groupResolver(inviterUserId, g.value)),
        );
        // Build a sync-returning resolver from the awaited results and re-run.
        const cachedResolver = (userId, groupName) => {
          const idx = groupEntries.findIndex(g => g.value === groupName);
          return idx >= 0 ? results[idx] : null;
        };
        result = shouldBlockInvite(inviterUserId, currentSettings, { groupResolver: cachedResolver });
      }

      // Race guard: bail if client was torn down during async lookup
      if (!client || !client.isLoggedIn || !client.isLoggedIn()) {
        logger.debug('[useInviteFiltering] Client gone during classification; skipping action');
        return;
      }
      // Race guard: bail if the room has already moved out of the invite state.
      // Use the event's `room` instance directly — its internal state is consistent
      // with the MyMembership event that was just dispatched. `client.getRoom(roomId)`
      // can return a stale cached room whose `getMyMembership()` is still `undefined`
      // at this microtask (cache lag), which would spuriously skip the auto-decline.
      const currentMembership = room.getMyMembership?.();
      if (currentMembership !== 'invite') {
        logger.debug(`[useInviteFiltering] Room ${room.roomId} membership changed to ${currentMembership}; skipping`);
        return;
      }

      // Fail-mode when result is null (ambiguous due to classification failure).
      if (result === null) {
        const mode = currentSettings.firstContactDefaultPermission || 'allow-all';
        // AllowAll => fail-open (don't block); BlockAll => fail-closed (block).
        const failMode = mode === 'allow-all' ? false : true;
        logger.warn(`[useInviteFiltering] Classification ambiguous for ${inviterUserId}; applying fail-${failMode ? 'closed' : 'open'} under ${mode}`);
        result = failMode;
      }

      if (result) {
        logger.info(`[useInviteFiltering] Auto-rejecting invite from ${inviterUserId} to room ${room.roomId}`);
        // UC12X-TRACE: dump the full settings + decision context at the
        // moment of auto-reject so we can see WHY a UC12X 2nd-device's
        // invite is being declined (currentSettings, classification
        // result, settings-sync state).
        logger.debug(`[UC12X-TRACE] AUTO-REJECT invite from=${inviterUserId} room=${room.roomId} settings=${JSON.stringify({defPerm: currentSettings.firstContactDefaultPermission, allowList: (currentSettings.firstContactAllowedList||[]).length, blockList: (currentSettings.firstContactBlockedList||[]).length})}`);
        const myUserId = client.getUserId();

        // Cross-HS auto-reject federation race fix (UC05 RCA 2026-05-23):
        // when the recipient HS calls federation `make_leave` against the
        // sender HS within ~50ms of the invite arriving, sender HS returns
        // 403 "not in room" because it hasn't fully persisted the invite
        // to its room state yet (Athena evidence on tenant=ns line at
        // 19:25:05.257 UTC). The leave never federates back to sender HS;
        // sender's invite-route poll exhausts at 30s seeing only invite;
        // test expects 403 but gets 200.
        //
        // Detect cross-HS by comparing room's host part with our own
        // homeserver domain. For cross-HS only, delay client.leave so the
        // invite has time to settle in sender HS's room state before
        // make_leave is dispatched. Within-org rooms aren't affected
        // (no federation make_leave) — no delay needed there.
        const myDomain = client.getDomain && client.getDomain();
        let roomDomain = null;
        const roomColon = room.roomId.indexOf(':');
        if (roomColon > 0) roomDomain = room.roomId.slice(roomColon + 1);
        const isCrossHsRoom = !!myDomain && !!roomDomain && roomDomain !== myDomain;

        // Capture invite event timing for [DeclineDIAG-Recipient] diag.
        const inviteEv = room.currentState?.getStateEvents?.('m.room.member', myUserId);
        const inviteEventTs = inviteEv && inviteEv.getTs ? inviteEv.getTs() : null;
        const tInviteSeen = Date.now();
        const inviteAgeMsAtDecision = inviteEventTs !== null ? (tInviteSeen - inviteEventTs) : null;

        // Pre-leave settle delay for cross-HS only. 4000ms gives the
        // sender HS enough time to fully persist its outbound invite to
        // room state BEFORE the recipient HS calls federation
        // `make_leave`. The observed race window was 38ms (Athena
        // tenant=ns 19:25:05.195→.233 UTC); 500ms was sufficient in a
        // single iso (2/2 pass) but lacks margin for production
        // synapse load-variance tails. 4s is ~105× the observed window.
        // Cost: ~26 cross-HS auto-rejects per broad cert run × 4s ≈
        // 104s of added driver-side wait time SUMMED across scenarios.
        // Since scenarios run in parallel via claimParallel, the per-run
        // wall-time impact is bounded by ~4-8s per scenario that hits
        // this branch — negligible.
        // If the invite is already old (handleInvite re-fired after
        // settings change, or invite was queued), no extra wait is
        // needed — `inviteAgeMsAtDecision` already accounts for that.
        let crossHsSettleWaitMs = 0;
        if (isCrossHsRoom) {
          const targetAgeMs = 4000;
          if (inviteAgeMsAtDecision === null || inviteAgeMsAtDecision < targetAgeMs) {
            crossHsSettleWaitMs = inviteAgeMsAtDecision === null
              ? targetAgeMs
              : Math.max(0, targetAgeMs - inviteAgeMsAtDecision);
          }
        }
        logger.info(`[DeclineDIAG-Recipient] auto-reject-pending room=${room.roomId} from=${inviterUserId} isCrossHsRoom=${isCrossHsRoom} myDomain=${myDomain || 'null'} roomDomain=${roomDomain || 'null'} inviteEventTs=${inviteEventTs} inviteAgeMsAtDecision=${inviteAgeMsAtDecision} crossHsSettleWaitMs=${crossHsSettleWaitMs}`);

        const tLeaveCallStart = Date.now();
        const doLeave = (waitMs) => {
          const issueLeave = () => {
            const tBeforeLeave = Date.now();
            logger.info(`[DeclineDIAG-Recipient] leave-call-start room=${room.roomId} waitedMs=${tBeforeLeave - tLeaveCallStart}`);
            client.leave(room.roomId)
              .then(() => {
                const tLeaveOk = Date.now();
                logger.debug(`[useInviteFiltering] Successfully rejected invite from ${inviterUserId}`);
                logger.debug(`[UC12X-TRACE] AUTO-REJECT-OK room=${room.roomId}`);
                // [DeclineDIAG-Recipient] leave-ok: capture local membership + state-event id
                // immediately after client.leave resolves. Distinguishes "leave succeeded
                // locally but federation dropped the leave" (sub-case B) from "leave threw
                // but membership was optimistically mutated" (anomaly). info-level so
                // [Device{N}-Browser] driver-side capture surfaces it.
                try {
                  const localMs = room.getMyMembership?.();
                  const localEv = room.currentState?.getStateEvents?.('m.room.member', myUserId);
                  logger.info(`[DeclineDIAG-Recipient] leave-ok room=${room.roomId} from=${inviterUserId} localMembership=${localMs} localEvId=${localEv?.getId?.() || null} localEvSender=${localEv?.getSender?.() || null} localEvTs=${localEv?.getTs?.() || null} tInviteSeen=${tInviteSeen} tBeforeLeave=${tBeforeLeave} tLeaveOk=${tLeaveOk} leaveDurationMs=${tLeaveOk - tBeforeLeave} inviteAgeAtLeaveStartMs=${inviteEventTs !== null ? (tBeforeLeave - inviteEventTs) : null}`);
                } catch (diagErr) {
                  logger.info(`[DeclineDIAG-Recipient] leave-ok room=${room.roomId} diag-failed: ${diagErr?.message || diagErr}`);
                }
              })
              .catch(err => {
                const tLeaveFail = Date.now();
                logger.error(`[useInviteFiltering] Failed to reject invite from ${inviterUserId}:`, err);
                logger.debug(`[UC12X-TRACE] AUTO-REJECT-FAIL room=${room.roomId} err=${err.message}`);
                // [DeclineDIAG-Recipient] leave-fail: capture errcode + httpStatus + err.data.
                // This is the signal that distinguishes sub-case A (recipient HS rejected
                // the leave, likely M_FORBIDDEN from federation make_leave 403) from any
                // other failure mode. info-level for driver-side capture survival.
                logger.info(`[DeclineDIAG-Recipient] leave-fail room=${room.roomId} from=${inviterUserId} errcode=${err?.errcode || err?.data?.errcode || 'none'} httpStatus=${err?.httpStatus || 'none'} msg=${err?.message || String(err)} data=${JSON.stringify(err?.data || null)} tBeforeLeave=${tBeforeLeave} tLeaveFail=${tLeaveFail} leaveDurationMs=${tLeaveFail - tBeforeLeave} inviteAgeAtLeaveStartMs=${inviteEventTs !== null ? (tBeforeLeave - inviteEventTs) : null}`);
              });
          };
          if (waitMs > 0) {
            setTimeout(issueLeave, waitMs);
          } else {
            issueLeave();
          }
        };
        doLeave(crossHsSettleWaitMs);
      } else {
        logger.debug(`[useInviteFiltering] Allowing invite from ${inviterUserId}`);
        // Mirror the AUTO-REJECT settings dump so post-mortem grep on
        // failed scenarios can distinguish "settings legitimately
        // allowed" from "settings cache hadn't hydrated yet → defaulted
        // to allow". The settings-cache race surfaced 2026-05-23 on
        // EC2 (fast-RTT) UC05 BlockAll: first cross-HS invite arrived
        // before defPerm propagated → ALLOW path silently taken → test
        // expected 403 but got 200. Without these fields a reader
        // can't tell ALLOW-by-policy from ALLOW-by-cold-cache.
        logger.debug(`[UC12X-TRACE] ALLOW invite from=${inviterUserId} room=${room.roomId} settings=${JSON.stringify({defPerm: currentSettings?.firstContactDefaultPermission, allowList: (currentSettings?.firstContactAllowedList||[]).length, blockList: (currentSettings?.firstContactBlockedList||[]).length, isReady})}`);
      }
    },
    [client],
  );

  useEffect(() => {
    if (!client) return;

    // The Matrix SDK tolerates async listeners (it ignores the returned promise), but we
    // wrap anyway to ensure unhandled rejections are logged rather than swallowed.
    const listener = (room, membership) => {
      Promise.resolve(handleInvite(room, membership)).catch(err => {
        logger.error('[useInviteFiltering] Unhandled error in handleInvite:', err);
      });
    };

    client.on(RoomEvent.MyMembership, listener);
    return () => {
      client.off(RoomEvent.MyMembership, listener);
    };
  }, [client, handleInvite]);

  // Re-evaluation pump: when isReady transitions false → true, sweep any
  // rooms still in 'invite' state and re-run handleInvite for each. Without
  // this, an invite that arrived during the load window would sit unresolved
  // forever (MyMembership only re-fires on actual membership changes; a settings
  // flip does not re-fire it). Cheap — typically zero or a small number of
  // pending invites per user, and runs at most once per ready transition.
  useEffect(() => {
    if (!isReady || !client) return;
    let rooms;
    try {
      rooms = client.getRooms();
    } catch (err) {
      logger.warn(`[useInviteFiltering] getRooms() failed in ready-pump: ${err?.message}`);
      return;
    }
    // UC12X-TRACE: log every ready-pump invocation. Pumps can fire MULTIPLE
    // times if isReady toggles (e.g. SSSS recovery causes a settings reload
    // → isReady flips false→true again). Each pump iterates ALL rooms in
    // invite state and calls handleInvite. We want to see how many pumps
    // happen and which rooms they re-process.
    const inviteRooms = rooms.filter(r => typeof r.getMyMembership === 'function' && r.getMyMembership() === 'invite');
    logger.debug(`[UC12X-TRACE] ready-pump fired: isReady=true, totalRooms=${rooms.length}, inviteRooms=${inviteRooms.length}, inviteRoomIds=[${inviteRooms.map(r => r.roomId).join(',')}]`);
    for (const room of inviteRooms) {
      Promise.resolve(handleInvite(room, 'invite')).catch(err => {
        logger.error('[useInviteFiltering] Ready-pump handleInvite error:', err);
      });
    }
  }, [isReady, client, handleInvite]);
};

export default useInviteFiltering;
